import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { LogOut, Users, Calendar, MessageSquare, Shield, UserCog, AlertCircle, UserPlus, UserCheck, Edit, Trash2, Power, Plus, BarChart, Video, LayoutDashboard, Menu, Sparkles, ClipboardCheck, Target, ShieldCheck, FileText, Mail } from "lucide-react";
import { hashPassword } from "@/lib/security";
import { handleRateLimitError } from "@/lib/rateLimitHandler";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";
import RecordingManager from "@/components/RecordingManager";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useActivityFeed } from "@/hooks/useActivityFeed";
import { BlogManager } from "@/components/admin/BlogManager";
import { FeedbackManager } from "@/components/admin/FeedbackManager";
import { ContactManager } from "@/components/admin/ContactManager";

interface User {
  id: string;
  email: string;
  display_name: string;
  created_at: string;
  role?: 'superadmin' | 'admin' | 'moderator' | 'user';
}

interface Meeting {
  id: string;
  title: string;
  description: string | null;
  meeting_code: string;
  host_id: string;
  is_active: boolean;
  created_at: string;
  host_name?: string;
}

interface ChatMessage {
  id: string;
  message: string;
  created_at: string;
  user_name: string;
  meeting_title?: string;
}

interface FeatureProgram {
  id: string;
  name: string;
  description: string;
  owner: string;
  stage: "alpha" | "beta" | "general";
  enabled: boolean;
  metric: string;
  category: "collaboration" | "governance" | "reliability";
  nextStep: string;
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { role, isSuperAdmin, isAdmin, loading: roleLoading } = useUserRole();
  const { toast } = useToast();
  const { addActivity } = useActivityFeed();
  const { t } = useTranslation();

  const roleDescriptions = useMemo(() => ({
    superadmin: {
      label: t("adminPanel.roles.superadmin.label"),
      color: 'destructive' as const,
      description: t("adminPanel.roles.superadmin.description"),
      permissions: [
        t("adminPanel.roles.superadmin.permissions.manageUsers"),
        t("adminPanel.roles.superadmin.permissions.assignRole"),
        t("adminPanel.roles.superadmin.permissions.deleteMeetings"),
        t("adminPanel.roles.superadmin.permissions.viewData"),
        t("adminPanel.roles.superadmin.permissions.systemConfig"),
      ],
    },
    admin: {
      label: t("adminPanel.roles.admin.label"),
      color: 'default' as const,
      description: t("adminPanel.roles.admin.description"),
      permissions: [
        t("adminPanel.roles.admin.permissions.manageUsers"),
        t("adminPanel.roles.admin.permissions.moderateMeetings"),
        t("adminPanel.roles.admin.permissions.viewAnalytics"),
        t("adminPanel.roles.admin.permissions.deleteContent"),
      ],
    },
    moderator: {
      label: t("adminPanel.roles.moderator.label"),
      color: 'secondary' as const,
      description: t("adminPanel.roles.moderator.description"),
      permissions: [
        t("adminPanel.roles.moderator.permissions.moderateMeetings"),
        t("adminPanel.roles.moderator.permissions.muteParticipants"),
        t("adminPanel.roles.moderator.permissions.viewReports"),
        t("adminPanel.roles.moderator.permissions.userSupport"),
      ],
    },
    user: {
      label: t("adminPanel.roles.user.label"),
      color: 'outline' as const,
      description: t("adminPanel.roles.user.description"),
      permissions: [
        t("adminPanel.roles.user.permissions.createMeetings"),
        t("adminPanel.roles.user.permissions.joinMeetings"),
        t("adminPanel.roles.user.permissions.sendMessages"),
        t("adminPanel.roles.user.permissions.manageProfile"),
      ],
    },
  }), [t]);
  
  const [users, setUsers] = useState<User[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserDisplayName, setNewUserDisplayName] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState<'superadmin' | 'admin' | 'moderator' | 'user'>('user');
  const [assignUserOpen, setAssignUserOpen] = useState(false);
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [editMeetingOpen, setEditMeetingOpen] = useState(false);
  const [deleteMeetingOpen, setDeleteMeetingOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [meetingTitle, setMeetingTitle] = useState("");
  const [meetingDescription, setMeetingDescription] = useState("");
  const [meetingPassword, setMeetingPassword] = useState("");
  const [createMeetingOpen, setCreateMeetingOpen] = useState(false);
  const [newMeetingHostId, setNewMeetingHostId] = useState<string>("");
  const featureProgramCatalog = useMemo<FeatureProgram[]>(() => [
    {
      id: "collab-cursors",
      name: t("adminPanel.featurePrograms.collabCursors.name"),
      description: t("adminPanel.featurePrograms.collabCursors.description"),
      owner: t("adminPanel.featurePrograms.collabCursors.owner"),
      stage: "beta",
      enabled: true,
      metric: t("adminPanel.featurePrograms.collabCursors.metric"),
      category: "collaboration",
      nextStep: t("adminPanel.featurePrograms.collabCursors.nextStep"),
    },
    {
      id: "ai-suggestions",
      name: t("adminPanel.featurePrograms.aiSuggestions.name"),
      description: t("adminPanel.featurePrograms.aiSuggestions.description"),
      owner: t("adminPanel.featurePrograms.aiSuggestions.owner"),
      stage: "alpha",
      enabled: true,
      metric: t("adminPanel.featurePrograms.aiSuggestions.metric"),
      category: "collaboration",
      nextStep: t("adminPanel.featurePrograms.aiSuggestions.nextStep"),
    },
    {
      id: "guided-validation",
      name: t("adminPanel.featurePrograms.guidedValidation.name"),
      description: t("adminPanel.featurePrograms.guidedValidation.description"),
      owner: t("adminPanel.featurePrograms.guidedValidation.owner"),
      stage: "beta",
      enabled: true,
      metric: t("adminPanel.featurePrograms.guidedValidation.metric"),
      category: "governance",
      nextStep: t("adminPanel.featurePrograms.guidedValidation.nextStep"),
    },
    {
      id: "version-approvals",
      name: t("adminPanel.featurePrograms.versionApprovals.name"),
      description: t("adminPanel.featurePrograms.versionApprovals.description"),
      owner: t("adminPanel.featurePrograms.versionApprovals.owner"),
      stage: "beta",
      enabled: false,
      metric: t("adminPanel.featurePrograms.versionApprovals.metric"),
      category: "governance",
      nextStep: t("adminPanel.featurePrograms.versionApprovals.nextStep"),
    },
    {
      id: "execution-playback",
      name: t("adminPanel.featurePrograms.executionPlayback.name"),
      description: t("adminPanel.featurePrograms.executionPlayback.description"),
      owner: t("adminPanel.featurePrograms.executionPlayback.owner"),
      stage: "alpha",
      enabled: false,
      metric: t("adminPanel.featurePrograms.executionPlayback.metric"),
      category: "reliability",
      nextStep: t("adminPanel.featurePrograms.executionPlayback.nextStep"),
    },
    {
      id: "offline-sync",
      name: t("adminPanel.featurePrograms.offlineSync.name"),
      description: t("adminPanel.featurePrograms.offlineSync.description"),
      owner: t("adminPanel.featurePrograms.offlineSync.owner"),
      stage: "alpha",
      enabled: true,
      metric: t("adminPanel.featurePrograms.offlineSync.metric"),
      category: "reliability",
      nextStep: t("adminPanel.featurePrograms.offlineSync.nextStep"),
    },
    {
      id: "marketplace-blocks",
      name: t("adminPanel.featurePrograms.marketplaceBlocks.name"),
      description: t("adminPanel.featurePrograms.marketplaceBlocks.description"),
      owner: t("adminPanel.featurePrograms.marketplaceBlocks.owner"),
      stage: "alpha",
      enabled: false,
      metric: t("adminPanel.featurePrograms.marketplaceBlocks.metric"),
      category: "collaboration",
      nextStep: t("adminPanel.featurePrograms.marketplaceBlocks.nextStep"),
    },
    {
      id: "data-retention",
      name: t("adminPanel.featurePrograms.dataRetention.name"),
      description: t("adminPanel.featurePrograms.dataRetention.description"),
      owner: t("adminPanel.featurePrograms.dataRetention.owner"),
      stage: "general",
      enabled: true,
      metric: t("adminPanel.featurePrograms.dataRetention.metric"),
      category: "governance",
      nextStep: t("adminPanel.featurePrograms.dataRetention.nextStep"),
    },
  ], [t]);

  const [featurePrograms, setFeaturePrograms] = useState<FeatureProgram[]>(featureProgramCatalog);

  useEffect(() => {
    setFeaturePrograms((prev) =>
      prev.map((feature) => {
        const updated = featureProgramCatalog.find((item) => item.id === feature.id);
        if (!updated) return feature;
        return {
          ...feature,
          name: updated.name,
          description: updated.description,
          owner: updated.owner,
          metric: updated.metric,
          nextStep: updated.nextStep,
          category: updated.category,
        };
      })
    );
  }, [featureProgramCatalog]);

  const toggleFeatureProgram = (featureId: string) => {
    setFeaturePrograms(prev => prev.map(feature =>
      feature.id === featureId ? { ...feature, enabled: !feature.enabled } : feature
    ));
  };

  const setFeatureStage = (featureId: string, stage: FeatureProgram["stage"]) => {
    setFeaturePrograms(prev => prev.map(feature =>
      feature.id === featureId ? { ...feature, stage } : feature
    ));
  };

  const fetchDirectoryUser = async (userId: string) => {
    const { data: rawData, error } = await supabase.functions.invoke("admin-tools", {
      body: {
        action: "get_user",
        payload: { userId }
      }
    });

    // Handle case where response may be a string instead of parsed JSON
    let data = rawData;
    if (typeof rawData === 'string') {
      try {
        data = JSON.parse(rawData);
      } catch {
        console.error("Failed to parse get_user response");
        return null;
      }
    }

    if (error || data?.error || !data?.user) {
      console.error("Fallback directory lookup failed", error || data?.error);
      return null;
    }

    return {
      email: data.user.email as string | null,
      display_name: (data.user as any).display_name as string | null,
      role: (data.user as any).role as string | null
    };
  };

  useEffect(() => {
    if (roleLoading || role === null) return;

    if (!isAdmin) {
      toast({
        title: t("adminPanel.toasts.accessDeniedTitle"),
        description: t("adminPanel.toasts.accessDeniedDesc"),
        variant: "destructive",
      });
      navigate("/");
      return;
    }

    if (isSuperAdmin) {
      fetchAdminData();
      return;
    }

    // Admins can access the analytics module without loading superadmin data
    setActiveTab("analytics");
    setLoading(false);
  }, [role, isSuperAdmin, isAdmin, roleLoading, navigate, toast]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      // Pull authoritative emails/roles from the admin tools Edge Function
      const { data: adminDirectoryRaw, error: directoryError } = await supabase.functions.invoke('admin-tools', {
        body: { action: 'list_users' }
      });

      if (directoryError) {
        console.error('Admin directory lookup failed', directoryError);
      }

      // Handle case where response may be a string instead of parsed JSON
      let adminDirectory = adminDirectoryRaw;
      if (typeof adminDirectoryRaw === 'string') {
        try {
          adminDirectory = JSON.parse(adminDirectoryRaw);
        } catch {
          console.error('Failed to parse admin directory response');
          adminDirectory = null;
        }
      }

      const directoryMap = new Map(
        (adminDirectory?.users || []).map((user: any) => [
          user.id,
          {
            email: user.email,
            role: user.role,
            display_name: user.display_name
          }
        ])
      );

      // Fetch users with their profiles and roles
      const { data: usersData, error: usersError } = await supabase
        .from("profiles")
        .select(`
          id,
          user_id,
          display_name,
          created_at
        `)
        .order("created_at", { ascending: false });

      if (usersError) throw usersError;

      // Fetch user roles separately
      const { data: rolesData, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id, role");

      if (rolesError) throw rolesError;

      // Create role map
      const roleMap = new Map();
      rolesData?.forEach(roleRecord => {
        roleMap.set(roleRecord.user_id, roleRecord.role);
      });

      const transformedUsers = await Promise.all((usersData || []).map(async (user) => {
        let directoryUser = directoryMap.get(user.user_id) as { email: string | null; role: string | null; display_name: string | null } | undefined;

        if (!directoryUser?.email || !directoryUser?.display_name) {
          const fallbackUser = await fetchDirectoryUser(user.user_id);
          if (fallbackUser) {
            directoryUser = {
              email: directoryUser?.email || fallbackUser.email,
              role: directoryUser?.role || fallbackUser.role,
              display_name: directoryUser?.display_name || fallbackUser.display_name
            };
            directoryMap.set(user.user_id, directoryUser);
          }
        }

        const roleFromDirectory = directoryUser?.role ? directoryUser.role : null;
        return {
          id: user.user_id,
          email: directoryUser?.email || t("adminPanel.fallbacks.emailUnavailable"),
          display_name: directoryUser?.display_name || user.display_name,
          created_at: user.created_at,
          role: (roleFromDirectory || roleMap.get(user.user_id) || 'user') as 'superadmin' | 'admin' | 'moderator' | 'user'
        };
      }));

      setUsers(transformedUsers);

      // Fetch meetings with host information
      const { data: meetingsData, error: meetingsError } = await supabase
        .from("meetings")
        .select(`
          id,
          title,
          description,
          meeting_code,
          host_id,
          is_active,
          created_at
        `)
        .order("created_at", { ascending: false })
        .limit(50);

      if (meetingsError) throw meetingsError;

      // Fetch host profiles separately
      const hostIds = meetingsData?.map(m => m.host_id) || [];
      const { data: hostProfiles, error: hostError } = await supabase
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", hostIds);

      if (hostError) throw hostError;

      const hostMap = new Map();
      hostProfiles?.forEach(profile => {
        hostMap.set(profile.user_id, profile.display_name);
      });

      const transformedMeetings = meetingsData?.map(meeting => ({
        ...meeting,
        host_name: hostMap.get(meeting.host_id) || t("adminPanel.fallbacks.unknown")
      })) || [];

      setMeetings(transformedMeetings);

      // Fetch recent chat messages
      const { data: chatData, error: chatError } = await supabase
        .from("chat_messages")
        .select(`
          id,
          message,
          created_at,
          user_id,
          meeting_id
        `)
        .order("created_at", { ascending: false })
        .limit(100);

      if (chatError) throw chatError;

      // Get user and meeting info for chat messages
      const userIds = [...new Set(chatData?.map(c => c.user_id) || [])];
      const meetingIds = [...new Set(chatData?.map(c => c.meeting_id) || [])];

      const { data: chatUsers, error: chatUsersError } = await supabase
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", userIds);

      const { data: chatMeetings, error: chatMeetingsError } = await supabase
        .from("meetings")
        .select("id, title")
        .in("id", meetingIds);

      if (chatUsersError || chatMeetingsError) throw chatUsersError || chatMeetingsError;

      const userMap = new Map();
      const meetingMap = new Map();
      
      chatUsers?.forEach(user => userMap.set(user.user_id, user.display_name));
      chatMeetings?.forEach(meeting => meetingMap.set(meeting.id, meeting.title));

      const transformedChat = chatData?.map(chat => ({
        ...chat,
        user_name: userMap.get(chat.user_id) || t("adminPanel.fallbacks.unknown"),
        meeting_title: meetingMap.get(chat.meeting_id) || t("adminPanel.fallbacks.unknownMeeting")
      })) || [];

      setChatMessages(transformedChat);

    } catch (error: any) {
      console.error('Error fetching admin data:', error);
      toast({
        title: t("adminPanel.toasts.errorTitle"),
        description: t("adminPanel.toasts.loadFailed"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'superadmin' | 'admin' | 'moderator' | 'user') => {
    try {
      const targetUser = users.find((u) => u.id === userId);

      // Delete existing role
      const { error: deleteError } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", userId);

      if (deleteError) throw deleteError;

      // Insert new role
      const { error: insertError } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role: newRole });

      if (insertError) throw insertError;

      toast({
        title: t("adminPanel.toasts.roleUpdatedTitle"),
        description: t("adminPanel.toasts.roleUpdatedDesc", { role: roleDescriptions[newRole].label }),
      });

      if (user) {
        addActivity({
          action: "assigned",
          actor: { id: user.id, name: user.email },
          target: {
            title: t("adminPanel.activity.roleAssigned", {
              role: roleDescriptions[newRole].label,
              name: targetUser?.display_name || t("adminPanel.fallbacks.user"),
            }),
          },
        });
      }

      // Refresh data
      fetchAdminData();
    } catch (error: any) {
      console.error('Error updating role:', error);
      toast({
        title: t("adminPanel.toasts.errorTitle"),
        description: t("adminPanel.toasts.roleUpdateFailed"),
        variant: "destructive",
      });
    }
  };

  const handleCreateUser = async () => {
    if (!newUserEmail.trim() || !newUserDisplayName.trim() || !newUserPassword.trim()) {
      toast({
        title: t("adminPanel.toasts.validationTitle"),
        description: t("adminPanel.toasts.userValidationDesc"),
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("admin-tools", {
        body: {
          action: "create_user",
          payload: {
            email: newUserEmail.trim(),
            password: newUserPassword.trim(),
            displayName: newUserDisplayName.trim(),
            role: newUserRole,
          },
        },
      });

      if (error || data?.error) {
        throw error || new Error(data?.error || t("adminPanel.toasts.createUserFailed"));
      }

      toast({
        title: t("adminPanel.toasts.userCreatedTitle"),
        description: t("adminPanel.toasts.userCreatedDesc", {
          name: newUserDisplayName,
          role: roleDescriptions[newUserRole].label,
        }),
      });

      setCreateUserOpen(false);
      setNewUserEmail("");
      setNewUserDisplayName("");
      setNewUserPassword("");
      setNewUserRole('user');
      fetchAdminData();
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast({
        title: t("adminPanel.toasts.errorTitle"),
        description: error?.message || t("adminPanel.toasts.createUserFailed"),
        variant: "destructive",
      });
    }
  };

  const generateMeetingCode = () => {
    const array = new Uint8Array(9);
    crypto.getRandomValues(array);
    
    // Convert to base36 (0-9, a-z) and uppercase
    return Array.from(array)
      .map(byte => (byte % 36).toString(36))
      .join('')
      .toUpperCase()
      .substring(0, 9);
  };

  const handleCreateMeeting = async () => {
    const selectedHost = newMeetingHostId || user?.id;

    if (!meetingTitle.trim() || !selectedHost) {
      toast({
        title: t("adminPanel.toasts.validationTitle"),
        description: t("adminPanel.toasts.meetingValidationDesc"),
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("admin-tools", {
        body: {
          action: "create_meeting",
          payload: {
            title: meetingTitle.trim(),
            description: meetingDescription.trim() || null,
            hostId: selectedHost,
            password: meetingPassword.trim() || null,
          },
        },
      });

      if (error || data?.error) {
        throw error || new Error(data?.error || t("adminPanel.toasts.createMeetingFailed"));
      }

      toast({
        title: t("adminPanel.toasts.meetingCreatedTitle"),
        description: t("adminPanel.toasts.meetingCreatedDesc", {
          code: data?.meeting?.meeting_code,
        }),
      });

      if (user) {
        addActivity({
          action: "created",
          actor: { id: user.id, name: user.email },
          target: { title: t("adminPanel.activity.meetingCreated", { title: meetingTitle }) },
        });
      }

      setCreateMeetingOpen(false);
      setMeetingTitle("");
      setMeetingDescription("");
      setMeetingPassword("");
      setNewMeetingHostId("");
      fetchAdminData();
    } catch (error: any) {
      console.error('Error creating meeting:', error);
      
      // Check if this is a rate limit error
      const isRateLimit = handleRateLimitError(error, 'meeting_create');
      
      if (!isRateLimit) {
        toast({
          title: t("adminPanel.toasts.errorTitle"),
          description: t("adminPanel.toasts.createMeetingFailed"),
          variant: "destructive",
        });
      }
    }
  };

  const handleEditMeeting = async () => {
    if (!selectedMeeting || !meetingTitle.trim()) {
      toast({
        title: t("adminPanel.toasts.validationTitle"),
        description: t("adminPanel.toasts.meetingTitleRequired"),
        variant: "destructive",
      });
      return;
    }

    try {
      const updateData: any = {
        title: meetingTitle,
        description: meetingDescription || null,
      };

      if (meetingPassword.trim()) {
        // Hash the new password before storing
        updateData.password = await hashPassword(meetingPassword);
      }

      const { error } = await supabase
        .from("meetings")
        .update(updateData)
        .eq("id", selectedMeeting.id);

      if (error) throw error;

      toast({
        title: t("adminPanel.toasts.meetingUpdatedTitle"),
        description: t("adminPanel.toasts.meetingUpdatedDesc"),
      });

      if (user) {
        addActivity({
          action: "edited",
          actor: { id: user.id, name: user.email },
          target: { title: t("adminPanel.activity.meetingEdited", { title: meetingTitle }) },
        });
      }

      setEditMeetingOpen(false);
      setSelectedMeeting(null);
      setMeetingTitle("");
      setMeetingDescription("");
      setMeetingPassword("");
      fetchAdminData();
    } catch (error: any) {
      console.error('Error updating meeting:', error);
      toast({
        title: t("adminPanel.toasts.errorTitle"),
        description: t("adminPanel.toasts.meetingUpdateFailed"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteMeeting = async () => {
    if (!selectedMeeting) return;

    try {
      const { error } = await supabase
        .from("meetings")
        .delete()
        .eq("id", selectedMeeting.id);

      if (error) throw error;

      toast({
        title: t("adminPanel.toasts.meetingDeletedTitle"),
        description: t("adminPanel.toasts.meetingDeletedDesc"),
      });

      setDeleteMeetingOpen(false);
      setSelectedMeeting(null);
      fetchAdminData();
    } catch (error: any) {
      console.error('Error deleting meeting:', error);
      toast({
        title: t("adminPanel.toasts.errorTitle"),
        description: t("adminPanel.toasts.meetingDeleteFailed"),
        variant: "destructive",
      });
    }
  };

  const handleToggleMeetingStatus = async (meetingId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from("meetings")
        .update({ is_active: !currentStatus })
        .eq("id", meetingId);

      if (error) throw error;

      toast({
        title: t("adminPanel.toasts.statusUpdatedTitle"),
        description: t("adminPanel.toasts.statusUpdatedDesc", {
          status: !currentStatus
            ? t("adminPanel.meetings.status.active")
            : t("adminPanel.meetings.status.ended"),
        }),
      });

      fetchAdminData();
    } catch (error: any) {
      console.error('Error updating meeting status:', error);
      toast({
        title: t("adminPanel.toasts.errorTitle"),
        description: t("adminPanel.toasts.statusUpdateFailed"),
        variant: "destructive",
      });
    }
  };

  const handleAssignUserToMeeting = async () => {
    if (!selectedMeetingId || !selectedUserId) {
      toast({
        title: t("adminPanel.toasts.validationTitle"),
        description: t("adminPanel.toasts.assignValidationDesc"),
        variant: "destructive",
      });
      return;
    }

    try {
      // Check if user is already a participant
      const { data: existingParticipant } = await supabase
        .from("meeting_participants")
        .select("id")
        .eq("meeting_id", selectedMeetingId)
        .eq("user_id", selectedUserId)
        .maybeSingle();

      if (existingParticipant) {
        toast({
          title: t("adminPanel.toasts.alreadyAssignedTitle"),
          description: t("adminPanel.toasts.alreadyAssignedDesc"),
        });
        return;
      }

      // Add user as participant
      const { error } = await supabase
        .from("meeting_participants")
        .insert({
          meeting_id: selectedMeetingId,
          user_id: selectedUserId,
          role: 'participant'
        });

      if (error) throw error;

      toast({
        title: t("adminPanel.toasts.userAssignedTitle"),
        description: t("adminPanel.toasts.userAssignedDesc"),
      });

      setAssignUserOpen(false);
      setSelectedMeetingId(null);
      setSelectedUserId("");
      fetchAdminData();
    } catch (error: any) {
      console.error('Error assigning user:', error);
      toast({
        title: t("adminPanel.toasts.errorTitle"),
        description: t("adminPanel.toasts.assignFailed"),
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const menuItems = useMemo(() => ([
    { id: "dashboard", label: t("adminPanel.menu.dashboard"), icon: LayoutDashboard },
    { id: "analytics", label: t("adminPanel.menu.analytics"), icon: BarChart },
    { id: "recordings", label: t("adminPanel.menu.recordings"), icon: Video },
    { id: "blog", label: t("adminPanel.menu.blog"), icon: FileText },
    { id: "feedback", label: t("adminPanel.menu.feedback"), icon: MessageSquare },
    { id: "contact", label: t("adminPanel.menu.contact"), icon: Mail },
    { id: "users", label: t("adminPanel.menu.users"), icon: Users },
    { id: "roles", label: t("adminPanel.menu.roles"), icon: Shield },
    { id: "meetings", label: t("adminPanel.menu.meetings"), icon: Calendar },
    { id: "chat", label: t("adminPanel.menu.chat"), icon: MessageSquare },
    { id: "innovations", label: t("adminPanel.menu.innovations"), icon: Sparkles },
  ]), [t]);

  const visibleMenuItems = isSuperAdmin
    ? menuItems
    : menuItems.filter(item => item.id === "analytics" || item.id === "blog");

  if (roleLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t("adminPanel.loading")}</p>
        </div>
      </div>
    );
  }

  const enabledFeatureCount = featurePrograms.filter(feature => feature.enabled).length;
  const betaFeatureCount = featurePrograms.filter(feature => feature.stage === "beta").length;
  const generalFeatureCount = featurePrograms.filter(feature => feature.stage === "general").length;

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <div className="fixed bottom-4 left-4 z-40 md:hidden">
          <SidebarTrigger className="h-12 w-12 rounded-full shadow-lg" aria-label={t("adminPanel.sidebar.openNavigation")} />
        </div>
        <Sidebar className="border-r">
          <SidebarContent>
            <div className="p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <Shield className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg">{t("adminPanel.sidebar.title")}</h2>
                  <p className="text-xs text-muted-foreground">{t("adminPanel.sidebar.subtitle")}</p>
                </div>
              </div>
            </div>
            
            <SidebarGroup>
              <SidebarGroupLabel>{t("adminPanel.sidebar.navigation")}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleMenuItems.map((item) => (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        onClick={() => setActiveTab(item.id)}
                        isActive={activeTab === item.id}
                        className="w-full"
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            
            <div className="mt-auto p-4 border-t">
              <div className="space-y-2">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start" 
                  onClick={() => navigate("/")}
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  {t("adminPanel.sidebar.dashboard")}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {t("adminPanel.sidebar.signOut")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("adminPanel.sidebar.signOutTitle")}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("adminPanel.sidebar.signOutDesc")}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction onClick={handleSignOut}>
                        {t("adminPanel.sidebar.signOutConfirm")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </SidebarContent>
        </Sidebar>

        <div className="flex-1 flex flex-col">
          <header className="h-16 border-b flex items-center px-6 bg-card">
            <SidebarTrigger className="mr-4" />
            <div className="flex items-center justify-between w-full">
              <div>
                <h1 className="text-xl font-semibold">
                  {menuItems.find(item => item.id === activeTab)?.label}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t("adminPanel.header.welcomeBack", { email: user?.email })}
                </p>
              </div>
            </div>
          </header>

          <main className="flex-1 p-6 overflow-auto">
            {activeTab === "dashboard" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t("adminPanel.stats.totalUsers")}</CardTitle>
                      <Users className="h-5 w-5 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-primary">{users.length}</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("adminPanel.stats.registeredAccounts")}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-secondary/10 to-secondary/5 border-secondary/20">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t("adminPanel.stats.totalMeetings")}</CardTitle>
                      <Calendar className="h-5 w-5 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-primary">{meetings.length}</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("adminPanel.stats.allTimeMeetings")}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t("adminPanel.stats.activeMeetings")}</CardTitle>
                      <Video className="h-5 w-5 text-accent" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-accent">
                        {meetings.filter(m => m.is_active).length}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("adminPanel.stats.currentlyLive")}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t("adminPanel.stats.chatMessages")}</CardTitle>
                      <MessageSquare className="h-5 w-5 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-primary">{chatMessages.length}</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("adminPanel.stats.totalMessages")}
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>{t("adminPanel.recentActivity.title")}</CardTitle>
                      <CardDescription>{t("adminPanel.recentActivity.description")}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {meetings.slice(0, 5).map((meeting) => (
                          <div key={meeting.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                            <div className="flex items-center space-x-3">
                              <div className={`h-2 w-2 rounded-full ${meeting.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                              <div>
                                <p className="font-medium text-sm">{meeting.title}</p>
                                <p className="text-xs text-muted-foreground">{meeting.host_name}</p>
                              </div>
                            </div>
                            <Badge variant={meeting.is_active ? "default" : "secondary"}>
                              {meeting.is_active
                                ? t("adminPanel.meetings.status.live")
                                : t("adminPanel.meetings.status.ended")}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle>{t("adminPanel.userDistribution.title")}</CardTitle>
                      <CardDescription>{t("adminPanel.userDistribution.description")}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(roleDescriptions).map(([roleKey, roleInfo]) => {
                          const count = users.filter(u => u.role === roleKey).length;
                          const percentage = users.length > 0 ? (count / users.length) * 100 : 0;
                          return (
                            <div key={roleKey} className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="font-medium">{roleInfo.label}</span>
                                <span className="text-muted-foreground">
                                  {t("adminPanel.userDistribution.userCount", { count })}
                                </span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-primary rounded-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
            
            {activeTab === "analytics" && (
              <AnalyticsDashboard />
            )}
            
            {activeTab === "recordings" && (
              <RecordingManager showMeetingTitle={true} />
            )}

            {activeTab === "blog" && (
              <BlogManager />
            )}

            {activeTab === "feedback" && (
              <FeedbackManager />
            )}

            {activeTab === "contact" && (
              <ContactManager />
            )}
            
            {activeTab === "users" && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <UserCog className="h-5 w-5" />
                      <CardTitle>{t("adminPanel.users.title")}</CardTitle>
                    </div>
                    <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
                      <DialogTrigger asChild>
                        <Button>
                          <UserPlus className="h-4 w-4 mr-2" />
                          {t("adminPanel.users.createUser")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t("adminPanel.users.dialog.title")}</DialogTitle>
                          <DialogDescription>
                            {t("adminPanel.users.dialog.description")}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="email">{t("adminPanel.users.form.emailLabel")}</Label>
                            <Input
                              id="email"
                              type="email"
                              placeholder={t("adminPanel.users.form.emailPlaceholder")}
                              value={newUserEmail}
                              onChange={(e) => setNewUserEmail(e.target.value)}
                            />
                          </div>
                        <div className="space-y-2">
                          <Label htmlFor="displayName">{t("adminPanel.users.form.displayNameLabel")}</Label>
                          <Input
                            id="displayName"
                            placeholder={t("adminPanel.users.form.displayNamePlaceholder")}
                            value={newUserDisplayName}
                            onChange={(e) => setNewUserDisplayName(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="password">{t("adminPanel.users.form.passwordLabel")}</Label>
                          <PasswordInput
                            id="password"
                            placeholder={t("adminPanel.users.form.passwordPlaceholder")}
                            value={newUserPassword}
                            onChange={(e) => setNewUserPassword(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="role">{t("adminPanel.users.form.roleLabel")}</Label>
                          <Select value={newUserRole} onValueChange={(value: any) => setNewUserRole(value)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">{roleDescriptions.user.label}</SelectItem>
                                <SelectItem value="moderator">{roleDescriptions.moderator.label}</SelectItem>
                                <SelectItem value="admin">{roleDescriptions.admin.label}</SelectItem>
                                <SelectItem value="superadmin">{roleDescriptions.superadmin.label}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setCreateUserOpen(false)}>
                            {t("common.cancel")}
                          </Button>
                          <Button onClick={handleCreateUser}>{t("adminPanel.users.dialog.createAction")}</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <CardDescription>
                    {t("adminPanel.users.description")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("adminPanel.users.table.displayName")}</TableHead>
                        <TableHead>{t("adminPanel.users.table.email")}</TableHead>
                        <TableHead>{t("adminPanel.users.table.role")}</TableHead>
                        <TableHead>{t("adminPanel.users.table.joined")}</TableHead>
                        <TableHead>{t("adminPanel.users.table.actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((userItem) => (
                        <TableRow key={userItem.id}>
                          <TableCell className="font-medium">
                            {userItem.display_name}
                          </TableCell>
                          <TableCell className="text-muted-foreground max-w-[200px]">
                            <span className="block truncate" title={userItem.email}>
                              {userItem.email}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant={roleDescriptions[userItem.role || 'user'].color}>
                              {roleDescriptions[userItem.role || 'user'].label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {new Date(userItem.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={userItem.role || 'user'}
                              onValueChange={(value) => handleRoleChange(userItem.id, value as any)}
                              disabled={userItem.id === user?.id}
                            >
                              <SelectTrigger className="w-[150px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="superadmin">{roleDescriptions.superadmin.label}</SelectItem>
                                <SelectItem value="admin">{roleDescriptions.admin.label}</SelectItem>
                                <SelectItem value="moderator">{roleDescriptions.moderator.label}</SelectItem>
                                <SelectItem value="user">{roleDescriptions.user.label}</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
            
            {activeTab === "roles" && (
              <div className="grid gap-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{t("adminPanel.roles.title")}</AlertTitle>
                  <AlertDescription>
                    {t("adminPanel.roles.description")}
                  </AlertDescription>
                </Alert>

                {Object.entries(roleDescriptions).map(([roleKey, roleInfo]) => (
                  <Card key={roleKey}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Shield className="h-5 w-5 text-primary" />
                          <CardTitle>{roleInfo.label}</CardTitle>
                        </div>
                        <Badge variant={roleInfo.color}>
                          {t("adminPanel.userDistribution.userCount", {
                            count: users.filter(u => u.role === roleKey).length,
                          })}
                        </Badge>
                      </div>
                      <CardDescription>{roleInfo.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold">{t("adminPanel.roles.permissionsLabel")}</p>
                        <ul className="space-y-1">
                          {roleInfo.permissions.map((permission, idx) => (
                            <li key={idx} className="text-sm text-muted-foreground flex items-center">
                              <span className="mr-2">•</span>
                              {permission}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            
            {activeTab === "meetings" && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{t("adminPanel.meetings.title")}</CardTitle>
                      <CardDescription>
                        {t("adminPanel.meetings.description")}
                      </CardDescription>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Dialog open={createMeetingOpen} onOpenChange={setCreateMeetingOpen}>
                        <DialogTrigger asChild>
                          <Button>
                            <Plus className="h-4 w-4 mr-2" />
                            {t("adminPanel.meetings.createAction")}
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>{t("adminPanel.meetings.createDialog.title")}</DialogTitle>
                            <DialogDescription>
                              {t("adminPanel.meetings.createDialog.description")}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="create-title">{t("adminPanel.meetings.form.titleLabel")}</Label>
                              <Input
                                id="create-title"
                                placeholder={t("adminPanel.meetings.form.titlePlaceholder")}
                                value={meetingTitle}
                                onChange={(e) => setMeetingTitle(e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="create-description">{t("adminPanel.meetings.form.descriptionLabel")}</Label>
                              <Input
                                id="create-description"
                                placeholder={t("adminPanel.meetings.form.descriptionPlaceholder")}
                                value={meetingDescription}
                                onChange={(e) => setMeetingDescription(e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="create-host">{t("adminPanel.meetings.form.hostLabel")}</Label>
                              <Select value={newMeetingHostId} onValueChange={setNewMeetingHostId}>
                                <SelectTrigger>
                                  <SelectValue placeholder={t("adminPanel.meetings.form.hostPlaceholder")} />
                                </SelectTrigger>
                                <SelectContent>
                                  {users.map((user) => (
                                    <SelectItem key={user.id} value={user.id}>
                                      {user.display_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="create-password">{t("adminPanel.meetings.form.passwordLabel")}</Label>
                              <PasswordInput
                                id="create-password"
                                placeholder={t("adminPanel.meetings.form.passwordPlaceholder")}
                                value={meetingPassword}
                                onChange={(e) => setMeetingPassword(e.target.value)}
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setCreateMeetingOpen(false)}>
                              {t("common.cancel")}
                            </Button>
                            <Button onClick={handleCreateMeeting}>{t("adminPanel.meetings.createDialog.createAction")}</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                      
                      <Dialog open={assignUserOpen} onOpenChange={setAssignUserOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline">
                            <UserCheck className="h-4 w-4 mr-2" />
                            {t("adminPanel.meetings.assignAction")}
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>{t("adminPanel.meetings.assignDialog.title")}</DialogTitle>
                            <DialogDescription>
                              {t("adminPanel.meetings.assignDialog.description")}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="meeting">{t("adminPanel.meetings.assignDialog.meetingLabel")}</Label>
                              <Select value={selectedMeetingId || ""} onValueChange={setSelectedMeetingId}>
                                <SelectTrigger>
                                  <SelectValue placeholder={t("adminPanel.meetings.assignDialog.meetingPlaceholder")} />
                                </SelectTrigger>
                                <SelectContent>
                                  {meetings.filter(m => m.is_active).map((meeting) => (
                                    <SelectItem key={meeting.id} value={meeting.id}>
                                      {meeting.title} ({meeting.meeting_code})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="user">{t("adminPanel.meetings.assignDialog.userLabel")}</Label>
                              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                                <SelectTrigger>
                                  <SelectValue placeholder={t("adminPanel.meetings.assignDialog.userPlaceholder")} />
                                </SelectTrigger>
                                <SelectContent>
                                  {users.map((user) => (
                                    <SelectItem key={user.id} value={user.id}>
                                      {user.display_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setAssignUserOpen(false)}>
                              {t("common.cancel")}
                            </Button>
                            <Button onClick={handleAssignUserToMeeting}>{t("adminPanel.meetings.assignDialog.assignAction")}</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("adminPanel.meetings.table.title")}</TableHead>
                        <TableHead>{t("adminPanel.meetings.table.code")}</TableHead>
                        <TableHead>{t("adminPanel.meetings.table.host")}</TableHead>
                        <TableHead>{t("adminPanel.meetings.table.status")}</TableHead>
                        <TableHead>{t("adminPanel.meetings.table.created")}</TableHead>
                        <TableHead>{t("adminPanel.meetings.table.actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {meetings.map((meeting) => (
                        <TableRow key={meeting.id}>
                          <TableCell className="font-medium">
                            {meeting.title}
                          </TableCell>
                          <TableCell className="font-mono">
                            {meeting.meeting_code}
                          </TableCell>
                          <TableCell>{meeting.host_name}</TableCell>
                          <TableCell>
                            <Badge variant={meeting.is_active ? 'default' : 'secondary'}>
                              {meeting.is_active
                                ? t("adminPanel.meetings.status.active")
                                : t("adminPanel.meetings.status.ended")}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {new Date(meeting.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleMeetingStatus(meeting.id, meeting.is_active)}
                                title={
                                  meeting.is_active
                                    ? t("adminPanel.meetings.actions.end")
                                    : t("adminPanel.meetings.actions.activate")
                                }
                              >
                                <Power className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedMeeting(meeting);
                                  setMeetingTitle(meeting.title);
                                  setMeetingDescription(meeting.description || "");
                                  setEditMeetingOpen(true);
                                }}
                                title={t("adminPanel.meetings.actions.edit")}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedMeeting(meeting);
                                  setDeleteMeetingOpen(true);
                                }}
                                title={t("adminPanel.meetings.actions.delete")}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
                
                <Dialog open={editMeetingOpen} onOpenChange={setEditMeetingOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t("adminPanel.meetings.editDialog.title")}</DialogTitle>
                      <DialogDescription>
                        {t("adminPanel.meetings.editDialog.description")}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-title">{t("adminPanel.meetings.form.titleLabel")}</Label>
                        <Input
                          id="edit-title"
                          placeholder={t("adminPanel.meetings.form.titlePlaceholder")}
                          value={meetingTitle}
                          onChange={(e) => setMeetingTitle(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-description">{t("adminPanel.meetings.form.descriptionLabel")}</Label>
                        <Input
                          id="edit-description"
                          placeholder={t("adminPanel.meetings.form.descriptionPlaceholder")}
                          value={meetingDescription}
                          onChange={(e) => setMeetingDescription(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-password">{t("adminPanel.meetings.form.newPasswordLabel")}</Label>
                        <PasswordInput
                          id="edit-password"
                          placeholder={t("adminPanel.meetings.form.newPasswordPlaceholder")}
                          value={meetingPassword}
                          onChange={(e) => setMeetingPassword(e.target.value)}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setEditMeetingOpen(false)}>
                        {t("common.cancel")}
                      </Button>
                      <Button onClick={handleEditMeeting}>{t("adminPanel.meetings.editDialog.saveAction")}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={deleteMeetingOpen} onOpenChange={setDeleteMeetingOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t("adminPanel.meetings.deleteDialog.title")}</DialogTitle>
                      <DialogDescription>
                        {t("adminPanel.meetings.deleteDialog.description", { title: selectedMeeting?.title })}
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDeleteMeetingOpen(false)}>
                        {t("common.cancel")}
                      </Button>
                      <Button variant="destructive" onClick={handleDeleteMeeting}>
                        {t("adminPanel.meetings.deleteDialog.deleteAction")}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </Card>
            )}

            {activeTab === "innovations" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t("adminPanel.innovations.enabledPrograms")}</CardTitle>
                      <Sparkles className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{enabledFeatureCount}</div>
                      <p className="text-xs text-muted-foreground">{t("adminPanel.innovations.enabledProgramsDesc")}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t("adminPanel.innovations.inBeta")}</CardTitle>
                      <ClipboardCheck className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{betaFeatureCount}</div>
                      <p className="text-xs text-muted-foreground">{t("adminPanel.innovations.inBetaDesc")}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t("adminPanel.innovations.generallyAvailable")}</CardTitle>
                      <ShieldCheck className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{generalFeatureCount}</div>
                      <p className="text-xs text-muted-foreground">{t("adminPanel.innovations.generallyAvailableDesc")}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t("adminPanel.innovations.governanceCoverage")}</CardTitle>
                      <Target className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const governanceTotal = featurePrograms.filter(feature => feature.category === "governance").length;
                        const governanceEnabled = featurePrograms.filter(feature => feature.category === "governance" && feature.enabled).length;
                        const coverage = governanceTotal ? Math.round((governanceEnabled / governanceTotal) * 100) : 0;
                        return (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-semibold">{coverage}%</span>
                              <span className="text-muted-foreground">
                                {t("adminPanel.innovations.programCount", {
                                  enabled: governanceEnabled,
                                  total: governanceTotal,
                                })}
                              </span>
                            </div>
                            <Progress value={coverage} />
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader className="flex items-start justify-between">
                    <div>
                      <CardTitle>{t("adminPanel.innovations.rolloutControlTitle")}</CardTitle>
                      <CardDescription>{t("adminPanel.innovations.rolloutControlDesc")}</CardDescription>
                    </div>
                    <Badge variant="secondary">{t("adminPanel.innovations.featureLab")}</Badge>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {featurePrograms.map(feature => (
                      <div key={feature.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{feature.name}</h3>
                              <Badge variant={feature.enabled ? "default" : "outline"}>
                                {t(`adminPanel.innovations.stages.${feature.stage}`)}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{feature.description}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Switch
                              checked={feature.enabled}
                              onCheckedChange={() => toggleFeatureProgram(feature.id)}
                              aria-label={t("adminPanel.innovations.toggleFeature", { name: feature.name })}
                            />
                            <Select value={feature.stage} onValueChange={(value: FeatureProgram["stage"]) => setFeatureStage(feature.id, value)}>
                              <SelectTrigger className="w-[140px]">
                              <SelectValue placeholder={t("adminPanel.innovations.stagePlaceholder")} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="alpha">{t("adminPanel.innovations.stages.alpha")}</SelectItem>
                                <SelectItem value="beta">{t("adminPanel.innovations.stages.beta")}</SelectItem>
                                <SelectItem value="general">{t("adminPanel.innovations.stages.general")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid gap-3 md:grid-cols-3 text-sm">
                          <div>
                            <p className="text-muted-foreground">{t("adminPanel.innovations.ownerLabel")}</p>
                            <p className="font-medium">{feature.owner}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">{t("adminPanel.innovations.successMetricLabel")}</p>
                            <p className="font-medium">{feature.metric}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">{t("adminPanel.innovations.nextStepLabel")}</p>
                            <p className="font-medium">{feature.nextStep}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                  <CardTitle>{t("adminPanel.innovations.guardrailsTitle")}</CardTitle>
                  <CardDescription>{t("adminPanel.innovations.guardrailsDesc")}</CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <h4 className="font-semibold">{t("adminPanel.innovations.governanceTitle")}</h4>
                      <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                        <li>{t("adminPanel.innovations.governanceItems.approvers")}</li>
                        <li>{t("adminPanel.innovations.governanceItems.evidence")}</li>
                        <li>{t("adminPanel.innovations.governanceItems.escalate")}</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold">{t("adminPanel.innovations.resilienceTitle")}</h4>
                      <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                        <li>{t("adminPanel.innovations.resilienceItems.incidentMode")}</li>
                        <li>{t("adminPanel.innovations.resilienceItems.redaction")}</li>
                        <li>{t("adminPanel.innovations.resilienceItems.queueEdits")}</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === "chat" && (
              <Card>
                <CardHeader>
                  <CardTitle>{t("adminPanel.chat.title")}</CardTitle>
                  <CardDescription>
                    {t("adminPanel.chat.description")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {chatMessages.map((message) => (
                      <div key={message.id} className="border-l-2 border-primary/20 pl-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{message.user_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(message.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm mb-1">{message.message}</p>
                        <span className="text-xs text-muted-foreground">
                          {t("adminPanel.chat.meetingLabel", { title: message.meeting_title })}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
